import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  classifyFilesApiV1BundleSplitterClassifyPost,
  classifyFilesByRequestApiV1BundleSplitterClassifyByRequestPost,
  getBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost,
  saveClassifiedFilesApiV1BundleSplitterCaseCaseNumberSavePost,
} from "@/api/generated/sdk.gen";
import type {
  BodyClassifyFilesApiV1BundleSplitterClassifyPost,
  BodyGetBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost,
  BundleSplitterSasTokenResponse,
  ClassifyFilesRequest,
  SaveFilesRequest,
  SaveFilesResponse,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Hook for getting a SAS token for uploading files to bundle splitter
 */
export const useGetBundleSplitterSasToken = () => {
  return useMutation({
    mutationFn: async (
      data: BodyGetBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost
    ): Promise<BundleSplitterSasTokenResponse> => {
      const response =
        await getBundleSplitterSasTokenApiV1BundleSplitterGetSasTokenPost({
          body: data,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to get SAS token: ${JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        throw new Error("No data returned from SAS token API");
      }

      return response.data;
    },
    onError: (error) => {
      console.error("Error getting SAS token:", error);
    },
  });
};

/**
 * Upload file directly to Azure blob storage using SAS URL
 */
export const uploadFileToAzureBlob = async (
  file: File,
  sasUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Set up progress tracking if callback provided
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `Failed to upload file to Azure blob storage: ${xhr.status} ${xhr.statusText} - ${xhr.responseText}`
          )
        );
      }
    });

    xhr.addEventListener("error", () => {
      reject(
        new Error(
          "Network error occurred while uploading file to Azure blob storage"
        )
      );
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("File upload was aborted"));
    });

    xhr.open("PUT", sasUrl);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader("Content-Type", file.type || "application/pdf");

    xhr.send(file);
  });
};

/**
 * Hook for uploading files directly to Azure blob storage
 */
export const useUploadToAzureBlob = () => {
  return useMutation({
    mutationFn: async ({
      file,
      sasUrl,
      onProgress,
    }: {
      file: File;
      sasUrl: string;
      onProgress?: (progress: number) => void;
    }) => {
      await uploadFileToAzureBlob(file, sasUrl, onProgress);
      return { success: true };
    },
    onError: (error) => {
      console.error("Error uploading to Azure blob:", error);
    },
  });
};

/**
 * Combined hook that handles the complete upload flow:
 * 1. Get SAS token
 * 2. Upload file to Azure blob storage
 * 3. Return the blob path for further processing
 */
export const useBundleSplitterUpload = () => {
  const getSasToken = useGetBundleSplitterSasToken();
  const uploadToBlob = useUploadToAzureBlob();

  const uploadFile = async (
    file: File,
    caseNumber: string,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    // Step 1: Get SAS token
    const sasTokenResponse = await getSasToken.mutateAsync({
      case_number: caseNumber,
      filename: file.name,
    });

    // Step 2: Upload file to Azure blob storage
    await uploadToBlob.mutateAsync({
      file,
      sasUrl: sasTokenResponse.sas_url,
      onProgress,
    });

    // Step 3: Return the blob path for classification
    return sasTokenResponse.blob_path;
  };

  return {
    uploadFile,
    isLoading: getSasToken.isPending || uploadToBlob.isPending,
    error: getSasToken.error || uploadToBlob.error,
    isSuccess: getSasToken.isSuccess && uploadToBlob.isSuccess,
    reset: () => {
      getSasToken.reset();
      uploadToBlob.reset();
    },
  };
};

/**
 * Hook for submitting files for classification via direct file upload
 */
export const useClassifyFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BodyClassifyFilesApiV1BundleSplitterClassifyPost) => {
      const response = await classifyFilesApiV1BundleSplitterClassifyPost({
        body: data,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to classify files: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Optionally invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });

      return data;
    },
    onError: (error) => {
      console.error("Error classifying files:", error);
    },
  });
};

/**
 * Hook for classifying files using file references instead of direct uploads
 */
export const useClassifyFilesByRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ClassifyFilesRequest) => {
      const response =
        await classifyFilesByRequestApiV1BundleSplitterClassifyByRequestPost({
          body: data,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to classify files by request: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Optionally invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });

      return data;
    },
    onError: (error) => {
      console.error("Error classifying files by request:", error);
    },
  });
};

/**
 * Hook for saving classified files from bundle splitter results
 */
export const useSaveClassifiedFiles = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: SaveFilesResponse) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      case_number,
      data,
    }: {
      case_number: string;
      data: SaveFilesRequest;
    }) => {
      const response =
        await saveClassifiedFilesApiV1BundleSplitterCaseCaseNumberSavePost({
          path: { case_number },
          body: data,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to save classified files: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch relevant queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
      // Pick up auto-mapped LOI questions once the background task lands.
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR],
      });

      onSuccess?.(data);
    },
    onError: (error) => {
      console.error("Error saving classified files:", error);
      onError?.(error);
    },
  });
};
